import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PetEPassport } from '@/pet/src/components/epassport/PetEPassport';
import { NoPetsEmptyState } from '@/pet/src/components/NoPetsEmptyState';
import { usePets } from '@/pet/src/context/PetsContext';
import type { Pet } from '@/pet/src/types/pet';

// Error boundary so E-Passport errors don't take down the whole app
function EPassportErrorContent({
  error,
  showDetail,
  onToggleDetail,
  onRetry,
  onRefresh,
}: {
  error: Error;
  showDetail: boolean;
  onToggleDetail: () => void;
  onRetry: () => void;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-white dark:bg-[#16151D] rounded-xl border border-gray-200 dark:border-white/10 shadow-lg">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        {t('epassportError.somethingWrong')}
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        {t('epassportError.couldNotLoad')}
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onToggleDetail}
          className="px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5"
        >
          {t('epassportError.showError')}
        </button>
        <button
          type="button"
          onClick={onRetry}
          className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
        >
          {t('epassportError.tryAgain')}
        </button>
        <button
          type="button"
          onClick={onRefresh}
          className="px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5"
        >
          {t('epassportError.refreshPage')}
        </button>
      </div>
      {showDetail && (
        <pre className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded text-xs overflow-auto max-h-32">
          {error.message}
        </pre>
      )}
    </div>
  );
}

class EPassportErrorBoundary extends React.Component<
  { children: React.ReactNode; pet: Pet; onRetry?: () => void },
  { error: Error | null; showDetail: boolean }
> {
  constructor(props: {
    children: React.ReactNode;
    pet: Pet;
    onRetry?: () => void;
  }) {
    super(props);
    this.state = { error: null, showDetail: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error('E-Passport error:', error);
  }

  render() {
    if (this.state.error) {
      return (
        <EPassportErrorContent
          error={this.state.error}
          showDetail={this.state.showDetail}
          onToggleDetail={() =>
            this.setState((s) => ({ ...s, showDetail: !s.showDetail }))
          }
          onRetry={() => {
            this.setState({ error: null, showDetail: false });
            this.props.onRetry?.();
          }}
          onRefresh={() => window.location.reload()}
        />
      );
    }
    return this.props.children;
  }
}

export const Route = createFileRoute('/dashboard/epassport')({
  component: PetsoEPassport,
});

function PetsoEPassport() {
  const { selectedPet, isLoading } = usePets();
  const [retryKey, setRetryKey] = React.useState(0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!selectedPet) {
    return <NoPetsEmptyState />;
  }

  return (
    <EPassportErrorBoundary
      key={retryKey}
      pet={selectedPet}
      onRetry={() => setRetryKey((k) => k + 1)}
    >
      <PetEPassport pet={selectedPet} />
    </EPassportErrorBoundary>
  );
}
